/**
 * One-off static renders of a theme (gallery thumbnails, level banners,
 * sharing cards). Paints straight onto the given canvas — no animation loop,
 * no caching.
 */
import type { ThemePack } from '@dropstack/core';
import { backingRatio, sceneSize } from './layout.ts';
import { paintScene } from './paintScene.ts';

export interface ThemePreviewOptions {
  /** CSS size to render at. Defaults to the canvas's laid-out size. */
  width?: number;
  height?: number;
  /** Backing-store ratio. Defaults to the device pixel ratio (capped at 2). */
  pixelRatio?: number;
  /** Scene time in seconds (default 0: clouds, balloons and birds in their rest pose). */
  time?: number;
  /** Include ambient particles (default true). */
  particles?: boolean;
}

/**
 * Render a static frame of `theme` into `canvas`, resizing its backing store
 * to width × height × pixel ratio. If the canvas has no layout size (e.g. it
 * isn't in the document) and no size is given, its current width/height
 * attributes are used as-is, at ratio 1.
 */
export function drawThemePreview(canvas: HTMLCanvasElement, theme: ThemePack, options: ThemePreviewOptions = {}): void {
  const cssW = options.width ?? canvas.clientWidth;
  const cssH = options.height ?? canvas.clientHeight;
  const laidOut = cssW > 0 && cssH > 0;
  const width = laidOut ? cssW : Math.max(1, canvas.width);
  const height = laidOut ? cssH : Math.max(1, canvas.height);
  const ratio = laidOut ? (options.pixelRatio ?? backingRatio(width, height, globalThis.devicePixelRatio)) : 1;
  canvas.width = Math.max(1, Math.round(width * ratio));
  canvas.height = Math.max(1, Math.round(height * ratio));
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  paintScene(ctx, theme, sceneSize(width, height), { time: options.time ?? 0, particles: options.particles ?? true });
}
